/**
 * Protected Route Component - Guards routes that require authentication.
 * Redirects to auth page if user is not logged in.
 */

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { buildAuthUrl, sanitizeReturnTo } from "@/lib/authFlow";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading, isAuthenticated, isPasswordRecovery } = useAuth();
  const location = useLocation();
  const returnTo = sanitizeReturnTo(location.pathname);

  if (isPasswordRecovery) {
    return <Navigate to={`${buildAuthUrl(returnTo, "signin")}&reset=true`} replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login but save the location they were trying to access
    return <Navigate to={buildAuthUrl(returnTo, user ? "signin" : "signup")} replace />;
  }

  return <>{children}</>;
};

/**
 * Public Route Component - Guards routes that should only be accessible when logged out.
 * Redirects to home if user is already logged in.
 */
export const PublicRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, loading, isPasswordRecovery } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isRecovery = isPasswordRecovery || params.get("reset") === "true"
    || new URLSearchParams(location.hash.slice(1)).get("type") === "recovery";

  if (!loading && isAuthenticated && !isRecovery) {
    return <Navigate to={sanitizeReturnTo(params.get("returnTo"))} replace />;
  }

  return <>{children}</>;
};
